import { Directive, inject, Injector, Input, OnInit } from '@angular/core';
import { Store } from '@sama/app-state';
import { matches } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CustomJsService } from '../core/h3ml/custom-js.service';
import { WorkspaceType } from '../core/h3ml/state/task-workspace-state';
import { TaskWorkspaceStore } from '../core/h3ml/state/task-workspace-store';
import { AnnotationLoadingService } from '../core/http/loading/annotation-loading.service';
import { FeatureFlagLoadingService } from '../core/http/loading/feature-flag-loading.service';
import { FeedbackLoadingService } from '../core/http/loading/feedback-loading.service';
import { LastRoundFeedbackLoadingService } from '../core/http/loading/last-round-feedback-loading-service';
import { LutLoadingService } from '../core/http/loading/lut-loading.service';
import { ProjectLoadingService } from '../core/http/loading/project-loading.service';
import {
  SettingsLoadingService,
  settingsStorageKey,
} from '../core/http/loading/settings-loading.service';
import { TaskLoadingService } from '../core/http/loading/task-loading.service';
import { RootShapeSelectionService } from '../core/outputs/annotation/annotation-shared/utils/root-shape-selection.service';
import { AnnotationTransientInfoService } from '../core/shared/misc-services/annotation-transient-info.service';
import { FeatureFlagService } from '../core/shared/misc-services/feature-flag.service';
import { TaxonomyLoadingService } from '../core/shared/misc-services/taxonomy-loading.service';
import { MessagingService } from '../cross-team-shared/messaging/messaging.service';
import { AuthorizationService } from '../cross-team-shared/misc-services/authorization.service';
import { BrowserControlService } from '../cross-team-shared/misc-services/browser-control.service';
import { Step } from '../team-apis/otterdam/step';
import { QaFeedback } from '../team-apis/otterdam/task';
import { User } from '../team-apis/otterdam/user';
import { EventTrackingService } from './event-tracking/event-tracking.service';
import {
  WorkspaceConfig,
  WorkspaceConfigService,
} from './misc-services/workspace-config.service';
import { WorkspaceLoadingService } from './misc-services/workspace-loading.service';

export type LoadedStatus = 'loaded' | 'loading' | 'error';

@Directive()
export abstract class AbstractWorkspaceComponent
  extends InjectableSuperclass
  implements OnInit
{
  // make sure all inputs are also in AbstractWorkspaceHostComponent
  @Input({ required: true }) currentUser!: User;

  isLoading$: Observable<boolean>;
  modelsLoading$ = new Subject<LoadedStatus>();

  store = inject(TaskWorkspaceStore);
  commentStore: Store<string | null>;
  isUsingGenAi$ = this.store.isUsingGenAi$();
  isUsingCategorization$ = this.store.isUsingCategorization$();

  protected annotationLoadingService = inject(AnnotationLoadingService);
  protected feedbackLoadingService = inject(FeedbackLoadingService);
  protected featureFlagLoadingService = inject(FeatureFlagLoadingService);
  protected lastRoundFeedbackLoadingService = inject(
    LastRoundFeedbackLoadingService,
  );
  protected messagingService = inject(MessagingService);
  protected projectLoadingService = inject(ProjectLoadingService);
  protected taskLoadingService = inject(TaskLoadingService);
  protected eventTrackingService = inject(EventTrackingService);
  protected featureFlagService = inject(FeatureFlagService);
  protected workspaceConfigService = inject(WorkspaceConfigService);
  protected browserControlService = inject(BrowserControlService);
  protected taxonomyService = inject(TaxonomyLoadingService);
  protected authorizationService = inject(AuthorizationService);
  protected lutLoadingService = inject(LutLoadingService);
  protected settingsLoadingService = inject(SettingsLoadingService);
  protected workspaceLoadingService = inject(WorkspaceLoadingService);
  protected annotationTransientInfoService = inject(
    AnnotationTransientInfoService,
  );

  private customJsService = inject(CustomJsService);
  private rootShapeSelectionService = inject(RootShapeSelectionService);

  private previouslyCompiledJs?: string;

  protected constructor(
    public name: WorkspaceType,
    public injector: Injector,
  ) {
    super(injector);

    this.isLoading$ = this.workspaceLoadingService.isLoading$;

    this.commentStore = this.getTaskQaFeedbackComment$();

    this.store('workspace').set(name);
  }

  async ngOnInit() {
    this.store('currentUser').set(this.currentUser);
    this.setWorkspaceConfig();

    try {
      await this.loadModels();
      this.handleModelsLoaded();
    } catch (error) {
      console.error(error);
      this.modelsLoading$.next('error');
    } finally {
      this.workspaceLoadingService.isLoading$.next(false);
    }

    this.loadAssets();
  }

  protected setStepFromStepId() {
    const stepId = this.workspaceConfigService.stepId;
    const step = this.store.state().project.steps.find(matches({ id: stepId }));
    if (!step) {
      throw new Error('That step does not exist on this project');
    }
    this.updateFromStepInfo(step);
  }

  protected updateFromStepInfo({ layout, js_code: jsCode }: Partial<Step>) {
    this.store.batch(() => {
      if (layout) {
        this.store('layout').set(layout);
      }
      if (jsCode !== undefined && jsCode !== this.previouslyCompiledJs) {
        this.store('customJs').set(this.customJsService.compile(jsCode));
      }
      this.previouslyCompiledJs = jsCode;
    });
  }

  protected handleModelsLoaded() {
    this.modelsLoading$.next('loaded');
    this.eventTrackingService.subscribeToWorkspaceFocusEvents(this);
    this.eventTrackingService.track(
      { id: 'load_workspace', bucket: 'untracked' },
      { isInteractive: false },
    );

    this.subscribeTo(
      this.store('settings').$.pipe(debounceTime(1000)),
      (settings) => {
        const key = settingsStorageKey(this.workspaceConfigService.projectId);
        localStorage.setItem(key, JSON.stringify(settings));
      },
    );
    this.annotationTransientInfoService.subscribeToChangesUpdatingTransientInfo();
    this.rootShapeSelectionService.loadSelectionFromQueryParameters();
  }

  protected loadPreAnnotationsIfAllowed() {
    if (!this.store.isUsingGenAi()) {
      return;
    }

    return this.annotationLoadingService.loadPreviousAnnotations();
  }

  private getTaskQaFeedbackComment$() {
    return this.store('task')<'qa_feedback', QaFeedback>('qa_feedback')(
      'comment',
    );
  }

  private loadAssets() {
    void this.lutLoadingService.load().catch(() => {
      this.store('hasFatalError').set(true);
    });
  }

  private setWorkspaceConfig() {
    Object.assign(this.workspaceConfigService, this.buildWorkspaceConfig());
    this.store('stepId').set(this.workspaceConfigService.stepId);

    const prototypeName = this.browserControlService.extractPrototypeName();
    this.eventTrackingService.addContext({
      workspace: this.name,
      project_id: this.workspaceConfigService.projectId,
      step_id: this.workspaceConfigService.stepId,
      user_id: () => this.store.state().currentUser.id,
      task_id: () => this.store.state().task.id,
      round: () => this.store.state().task.round,
      ...(prototypeName && { prototype_name: prototypeName }),
    });
  }

  protected abstract buildWorkspaceConfig(): WorkspaceConfig;
  protected abstract loadModels(): Promise<void>;
}
